import { useState, useMemo, useCallback } from "react";
import { Card, CardContent } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { useToast } from "@shared/hooks/use-toast";
import { useParams, useSearchParams } from "react-router-dom";
import { useComplianceHistory } from "@features/ax/assetRegistry/networks/hooks";
import { PageHeader } from "@shared/components/PageHeader";
import { ComplianceReportDialog } from "@features/ax/shared/complianceReport";
import { Button } from "@shared/ui/button";
import { Eye } from "lucide-react";

export const HistoryLanding = () => {
  const { id } = useParams();
  const { data: historyData } = useComplianceHistory({
    entity_id: id,
    entity_type: 5,
  });
  const { toast } = useToast();
  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedReportData, setSelectedReportData] = useState();

  const handleViewReport: (row: any) => void = (row: any) => {
    setIsDialogOpen(true);
    setSelectedReportData(row);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedReportData(null);
  };

  const complianceHistoryColumns: TableColumn<any>[] = [
    {
      key: "reportDate",
      header: "Report Date",
      type: "text",
      render: (value) => <span className="font-medium">{value}</span>,
    },
    {
      key: "analysisPeriod",
      header: "Analysis Period",
      type: "text",
      render: (value) => <span className="text-sm text-gray-600">{value}</span>,
    },
    {
      key: "parametersMonitored",
      header: "Parameters Monitored",
      type: "custom",
      render: (value) => (
        <Badge variant="outline" className="bg-blue-50 text-blue-700">
          {value}
        </Badge>
      ),
    },
    {
      key: "inCompliance",
      header: "In Compliance",
      type: "custom",
      render: (value) => (
        <Badge className="bg-green-100 text-green-800">{value}</Badge>
      ),
    },
    {
      key: "violations",
      header: "Violations",
      type: "custom",
      render: (value) => (
        <Badge
          className={
            value > 0
              ? "bg-red-100 text-red-800"
              : "bg-green-100 text-green-800"
          }
        >
          {value}
        </Badge>
      ),
    },
    {
      key: "assessedBy",
      header: "Assessed By",
      type: "text",
      render: (value) => <span className="text-gray-600">{value}</span>,
    },
    {
      key: "actions",
      header: "Actions",
      render: (value, row) => {
        return (
          <div className="flex items-center gap-2">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => handleViewReport(row)}
              tooltip="View Report"
            >
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  const complianceTotalItems = historyData?.results?.length || 0;
  const complianceTotalPages = Math.ceil(complianceTotalItems / PAGE_SIZE);
  const complianceStartIndex = (currentPage - 1) * PAGE_SIZE;

  const compliancePagination = useMemo(
    () => ({
      currentPage: currentPage,
      totalPages: complianceTotalPages,
      totalItems: complianceTotalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: complianceStartIndex + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, complianceTotalItems),
    }),
    [
      currentPage,
      complianceTotalPages,
      complianceTotalItems,
      complianceStartIndex,
    ]
  );

  const handleCompliancePageChange = useCallback(
    (newPage: number) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", newPage.toString());

      setSearchParams(newParams, { replace: true });
      setCurrentPage(newPage);
    },
    [searchParams, setSearchParams]
  );
  return (
    <div className="space-y-6">
      {/* Compliance Calculation History */}
      <Card>
        <PageHeader
          title="Compliance Calculation History"
          subtitle="Historical record of calculated compliance reports"
        />
        <CardContent className="p-0 mt-3">
          <GenericTable
            data={historyData?.results || []}
            columns={complianceHistoryColumns}
            rowKey="id"
            pagination={compliancePagination}
            onPageChange={handleCompliancePageChange}
            emptyMessage="No compliance history found"
          />
        </CardContent>
      </Card>
      <ComplianceReportDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        reportData={selectedReportData}
      />
    </div>
  );
};
